import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { productAttributeFormSchema } from "@/lib/schemas/product-attribute-schemas";
import { ProductAttribute } from "@/types/product-type";

interface ProductAttributeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: any) => void;
  attribute?: ProductAttribute | null;
  isLoading?: boolean;
  productId: string; // productId as string to preserve precision
}

export const ProductAttributeFormDialog = ({
  open,
  onOpenChange,
  onSubmit,
  attribute,
  isLoading = false,
  productId,
}: ProductAttributeFormDialogProps) => {
  const isEditing = !!attribute;

  const form = useForm<any>({
    resolver: zodResolver(productAttributeFormSchema),
    defaultValues: {
      productId: productId,
      name: "",
      displayName: "",
      type: "color",
      isActive: true,
    },
  });

  // Reset form when attribute changes
  useEffect(() => {
    if (attribute) {
      form.reset({
        productId: productId,
        name: attribute.name,
        displayName: attribute.displayName || "",
        type: "color",
        isActive: attribute.isActive || true,
      });
    } else {
      form.reset({
        productId: productId,
        name: "",
        displayName: "",
        type: "color",
        isActive: true,
      });
    }
  }, [attribute, form, productId]);

  const handleSubmit = (values: any) => {
    onSubmit(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Attribute" : "Add New Attribute"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the details for this attribute"
              : "Create a new attribute for your products"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name (System)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="color"
                      {...field}
                      disabled={isEditing} // Prevent changing system name when editing
                    />
                  </FormControl>
                  <FormDescription>
                    Internal name used in the system (lowercase, no spaces)
                    {isEditing && " - Cannot be changed after creation"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Color" {...field} />
                  </FormControl>
                  <FormDescription>
                    Name shown to users (optional)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Attribute Type</FormLabel>
                  <FormControl>
                    <select
                      className="w-full h-10 px-3 border rounded-md"
                      {...field}
                    >
                      <option value="color">Color</option>
                      <option value="size">Size</option>
                      <option value="material">Material</option>
                      <option value="other">Other</option>
                    </select>
                  </FormControl>
                  <FormDescription>
                    Defines the behavior of this attribute
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Make this attribute available for products
                    </FormDescription>
                  </div>
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      className="accent-blue-500 h-5 w-5"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || form.formState.isSubmitting}
              >
                {isLoading || form.formState.isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEditing ? "Updating..." : "Creating..."}
                  </div>
                ) : isEditing ? (
                  "Update Attribute"
                ) : (
                  "Create Attribute"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
